import { $fetch, createFetch, FetchOptions, $Fetch } from 'ofetch';

import { deepTransformKeys, toCamelCase, toSnakeCase } from '../utils';
import { env } from '../constants';
import { ContentType } from '../constants/request';

function transformFormDataKeys(data: FormData, transformer: (key: string) => string) {
  const formData = new FormData();

  for (const [key, value] of data.entries()) {
    const newKey = transformer(key);

    if (value instanceof File) formData.set(newKey, value, value.name);
    else formData.set(newKey, value);
  }

  return formData;
}

let myFetch: $Fetch;
let fetchInstance: $Fetch;
const fetchOptions: FetchOptions = {
  baseURL: env.API_URL,
  method: 'get',
  headers: {
    Accept: ContentType.JSON,
  },

  onResponse: ({ response }) => {
    const { _data: data } = response;
    const isJsonResponse = response.headers.get('content-type')?.includes(ContentType.JSON);

    // eslint-disable-next-line no-underscore-dangle
    response._data = isJsonResponse && data ? deepTransformKeys(data, toCamelCase) : data;
  },

  onRequest: ({ options }) => {
    const { body, params, headers } = options;
    const csrf = useCookie<string>('_csrf_token');

    if (headers && csrf.value) {
      Object.assign(
        headers,
        { 'X-CSRF-Token': csrf.value },
      );
    }

    let newParams = params;

    if (params) {
      newParams = deepTransformKeys(params, toSnakeCase);
    }

    // eslint-disable-next-line no-param-reassign
    options.params = newParams;

    if (!body) return;
    if (Array.isArray(headers) || !headers) return;

    let newBody = body;
    const contentType = headers instanceof Headers ? headers.get('Content-Type') : headers['Content-Type'];
    const isFormData = body instanceof FormData;
    const isJsonRequest = !!contentType?.includes(ContentType.JSON) || !isFormData;

    if (isFormData) {
      newBody = transformFormDataKeys(body, toSnakeCase);
    } else if (isJsonRequest) {
      newBody = deepTransformKeys(body, toSnakeCase);
    }

    // eslint-disable-next-line no-param-reassign
    options.body = newBody;
  },

  onResponseError({ response, error }) {
    if (response.status !== 410) return;

    showError(createError({ statusCode: 410, fatal: true, cause: error, statusMessage: 'Gone' }));
  },
};

function initializeCustomFetch() {
  myFetch ??= createFetch({
    fetch: globalThis.fetch,
    Headers: globalThis.Headers,
  });

  fetchInstance ??= myFetch.create(fetchOptions);
}

const originalHttp = $fetch.create(fetchOptions);
const proxiedHttp = new Proxy(originalHttp, {
  get(target, prop, receiver) {
    initializeCustomFetch();

    if (prop === 'raw') return Reflect.get(fetchInstance, prop, receiver);

    // For other properties, just return the original value
    return Reflect.get(target, prop, receiver);
  },

  apply(_, thisArg, argumentsList) {
    initializeCustomFetch();

    return Reflect.apply(fetchInstance, thisArg, argumentsList);
  },
});

// Remove everything related to the proxiedHttp when issue bellow if fixed
// https://github.com/unjs/ofetch/issues/295
export const http = import.meta.env.MODE === 'test' ? proxiedHttp : originalHttp;
